// src/components/Dapp.js (업데이트 버전)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Wallet, 
  Send, 
  Coins, 
  ArrowLeft, 
  CheckCircle, 
  AlertCircle,
  Gift,
  ChevronLeft,
  Image as ImageIcon
} from 'lucide-react';
import TokenAirdrop from './TokenAirdrop';
import TokenTransfer from './TokenTransfer';
import NFTMinting from './NFTMinting'; // NFT 발행 컴포넌트 추가
import VisitStats from './VisitStats';
import MyNFT from './MyNFT';  
import NFTGallery from './NFTGallery'; 
import './Dapp.css';

const Dapp = ({ user, userProfile }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [account, setAccount] = useState('');
  const [balance, setBalance] = useState('0');
  const [isConnecting, setIsConnecting] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [availableWallets, setAvailableWallets] = useState([]);
  const [showWalletSelector, setShowWalletSelector] = useState(false);
  const navigate = useNavigate();

  // WorldLand 네트워크 설정
  const WORLDLAND_NETWORK = {
    chainId: '0x67', // 103 in hex
    chainName: 'WorldLand',
    nativeCurrency: {
      name: 'WorldLand Coin',
      symbol: 'WLC',
      decimals: 18
    },
    rpcUrls: ['https://seoul.worldland.foundation/'],
    blockExplorerUrls: ['https://explorer.worldland.foundation/']
  };

  const dappFeatures = [
    {
      icon: ImageIcon,
      title: 'My NFT',
      description: '소유하시 NFT들을 조회할 수 있습니다. NFT 전송은 월드랜드 전용지갑을 사용해주세요.',
      onClick: () => setSelectedFeature('mynft'),
      requiresWallet: true
    },
    {
      icon: ImageIcon,
      title: 'NFT Gallery',
      description: 'WorldLand 블록체인의 모든 NFT 컬렉션을 탐색하고 확인할 수 있습니다.',
      onClick: () => setSelectedFeature('gallery'),
      requiresWallet: false
    },
    {
      icon: Gift,
      title: 'Token Airdrop',
      description: '보유한 토큰이나 WLC 코인을 여러 주소에 동시에 에어드랍할 수 있습니다.',
      onClick: () => setSelectedFeature('airdrop'),
      requiresWallet: true
    },
    {
      icon: Send,
      title: 'Token Transfer',
      description: '토큰을 안전하게 다른 주소로 전송할 수 있습니다.',
      onClick: () => setSelectedFeature('transfer'),
      requiresWallet: true
    },      
    {
      icon: ImageIcon,
      title: 'NFT Minting',
      description: '나만의 NFT를 발행하고 WorldLand 블록체인에 기록할 수 있습니다. Under Construction.',
      onClick: () => setSelectedFeature('nft'),
      requiresWallet: true
    },
    {
      icon: Coins,
      title: 'Token Info',
      description: '토큰의 상세 정보와 보유량을 확인할 수 있습니다.',
      onClick: () => setSelectedFeature('info'),
      requiresWallet: false
    }
  ];

  // 페이지 로드 시 스크롤을 맨 위로 이동
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    detectAvailableWallets();
  }, []);

  // 사용 가능한 지갑 감지
  const detectAvailableWallets = () => {
    const wallets = [];
    
    if (window.ethereum && window.ethereum.isMetaMask && !window.ethereum.isPhantom) {
      wallets.push({
        name: 'MetaMask',
        provider: window.ethereum,
        icon: '🦊',
        id: 'metamask'
      });
    }
    
    if (window.phantom && window.phantom.ethereum) {
      wallets.push({
        name: 'Phantom',
        provider: window.phantom.ethereum,
        icon: '👻',
        id: 'phantom'
      });
    }
    
    if (window.ethereum && window.ethereum.providers) {
      window.ethereum.providers.forEach((provider) => {
        if (provider.isMetaMask && !provider.isPhantom) {
          const existingMetaMask = wallets.find(w => w.id === 'metamask');
          if (!existingMetaMask) {
            wallets.push({
              name: 'MetaMask',
              provider: provider,
              icon: '🦊',
              id: 'metamask'
            });
          }
        } else if (provider.isPhantom) {
          const existingPhantom = wallets.find(w => w.id === 'phantom');
          if (!existingPhantom) {
            wallets.push({
              name: 'Phantom',
              provider: provider,
              icon: '👻',
              id: 'phantom'
            });
          }
        }
      });
    }
    
    if (wallets.length === 0 && window.ethereum) {
      wallets.push({
        name: 'Unknown Wallet',
        provider: window.ethereum,
        icon: '💼',
        id: 'unknown'
      });
    }
    
    console.log('감지된 지갑들:', wallets);
    setAvailableWallets(wallets);
  };

  // 특정 지갑으로 연결
  const connectWithWallet = async (wallet) => {
    setIsConnecting(true);
    setShowWalletSelector(false);
    
    try {
      console.log(`${wallet.name} 지갑으로 연결 시도 중...`, wallet);
      
      let provider = wallet.provider;
      
      if (wallet.id === 'metamask') {
        if (window.ethereum.providers) {
          const metamaskProvider = window.ethereum.providers.find(p => p.isMetaMask && !p.isPhantom);
          if (metamaskProvider) {
            provider = metamaskProvider;
          }
        } else if (window.ethereum.isMetaMask && !window.ethereum.isPhantom) {
          provider = window.ethereum;
        }
      }
      
      if (wallet.id === 'phantom') {
        if (window.phantom && window.phantom.ethereum) {
          provider = window.phantom.ethereum;
        }
      }
      
      console.log('사용할 provider:', provider);
      
      await switchToWorldLand(provider);
      
      try {
        const accounts = await provider.request({ method: 'eth_accounts' });
        
        if (accounts.length > 0) {
          console.log('🔄 기존 연결된 계정 발견, 새로운 연결 요청...');
          
          const newAccounts = await provider.request({ 
            method: 'wallet_requestPermissions',
            params: [{ eth_accounts: {} }]
          }).then(() => provider.request({ method: 'eth_requestAccounts' }));
          
          console.log('✅ 새로운 계정 선택:', newAccounts);
        } else {
          console.log('🆕 첫 연결, 계정 선택 요청...');
        }
      } catch (permissionError) {
        console.log('⚠️ 권한 재요청 실패, 일반 연결 시도:', permissionError);
      }
      
      const accounts = await provider.request({ 
        method: 'eth_requestAccounts',
        params: []
      });
      
      if (accounts.length === 0) {
        throw new Error('계정을 찾을 수 없습니다.');
      }
      
      setAccount(accounts[0]);
      setIsConnected(true);
      
      window.selectedEthereumProvider = provider;
      
      await getBalance(accounts[0], provider);
      
      console.log(`${wallet.name} 연결 성공:`, accounts[0]);
      
    } catch (error) {
      console.error(`${wallet.name} 연결 중 오류:`, error);
      
      if (error.code === 4001) {
        alert('지갑 연결이 거부되었습니다.');
      } else if (error.code === -32002) {
        alert('이미 지갑 연결 요청이 진행 중입니다. 지갑 창을 확인해주세요.');
      } else {
        alert(`${wallet.name} 연결에 실패했습니다: ${error.message}`);
      }
    } finally {
      setIsConnecting(false);
    }
  };

  // 잔액 조회
  const getBalance = async (address, provider = window.selectedEthereumProvider || window.ethereum) => {
    try {
      const balance = await provider.request({
        method: 'eth_getBalance',
        params: [address, 'latest'],
      });
      const balanceInEther = parseInt(balance, 16) / Math.pow(10, 18);
      setBalance(balanceInEther.toFixed(4));
    } catch (error) {
      console.error('잔액 조회 중 오류:', error);
    }
  };

  // 네트워크 추가
  const addWorldLandNetwork = async (provider = window.selectedEthereumProvider || window.ethereum) => {
    try {
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [WORLDLAND_NETWORK],
      });
    } catch (error) {
      console.error('네트워크 추가 중 오류:', error);
      throw error;
    }
  };

  // 네트워크 전환
  const switchToWorldLand = async (provider = window.selectedEthereumProvider || window.ethereum) => {
    try {
      await provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: WORLDLAND_NETWORK.chainId }],
      });
    } catch (error) {
      if (error.code === 4902) {
        await addWorldLandNetwork(provider);
      } else {
        throw error;
      }
    }
  };

  // 지갑 연결 시작
  const connectWallet = async () => {
    if (availableWallets.length === 0) {
      alert('지원되는 지갑이 설치되어 있지 않습니다. MetaMask를 설치해주세요.');
      window.open('https://metamask.io/download/', '_blank');
      return;
    }

    if (availableWallets.length === 1) {
      await connectWithWallet(availableWallets[0]);
    } else {
      setShowWalletSelector(true);
    }
  };

  // 지갑 연결 해제
  const disconnectWallet = async () => {
    try {
      console.log('🔌 지갑 연결 해제 시작...');
      
      setAccount('');
      setIsConnected(false);
      setBalance('0');
      setSelectedFeature(null);
      
      if (window.selectedEthereumProvider) {
        delete window.selectedEthereumProvider;
      }
      
      try {
        localStorage.removeItem('walletconnect');
        localStorage.removeItem('WALLETCONNECT_DEEPLINK_CHOICE');
        localStorage.removeItem('metamask.selectedAddress');
        localStorage.removeItem('metamask.isConnected');
        localStorage.removeItem('-walletlink:https://www.walletlink.org:version');
        localStorage.removeItem('-walletlink:https://www.walletlink.org:session:id');
        localStorage.removeItem('-walletlink:https://www.walletlink.org:session:secret');
        localStorage.removeItem('-walletlink:https://www.walletlink.org:session:linked');
        localStorage.removeItem('wagmi.cache');
        localStorage.removeItem('wagmi.store');
        localStorage.removeItem('walletAddress');
        localStorage.removeItem('connectedWallet');
        localStorage.removeItem('isWalletConnected');
        localStorage.removeItem('phantom.ethereum.connected');
        
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (
            key.includes('wallet') || 
            key.includes('metamask') || 
            key.includes('ethereum') ||
            key.includes('web3') ||
            key.includes('phantom') ||
            key.includes('coinbase')
          )) {
            keysToRemove.push(key);
          }
        }
        
        keysToRemove.forEach(key => {
          localStorage.removeItem(key);
          console.log(`🗑️ localStorage 삭제: ${key}`);
        });
        
        console.log('✅ localStorage 지갑 정보 삭제 완료');
      } catch (storageError) {
        console.warn('⚠️ localStorage 정리 중 오류:', storageError);
      }
      
      try {
        sessionStorage.removeItem('walletconnect');
        sessionStorage.removeItem('connectedWallet');
        sessionStorage.removeItem('walletAddress');
        console.log('✅ sessionStorage 지갑 정보 삭제 완료');
      } catch (sessionError) {
        console.warn('⚠️ sessionStorage 정리 중 오류:', sessionError);
      }
      
      if (window.ethereum) {
        try {
          console.log('🔄 Ethereum provider 상태 초기화...');
          
          if (window.ethereum.removeAllListeners) {
            window.ethereum.removeAllListeners();
          }
          
        } catch (providerError) {
          console.warn('⚠️ Provider 정리 중 오류:', providerError);
        }
      }
      
      const shouldRefresh = window.confirm(
        '지갑 연결이 해제되었습니다.\n\n완전한 초기화를 위해 페이지를 새로고침하시겠습니까?\n(취소하면 현재 상태를 유지합니다)'
      );
      
      if (shouldRefresh) {
        window.location.reload();
      } else {
        console.log('✅ 지갑 연결 해제 완료 (새로고침 없음)');
        alert('지갑 연결이 해제되었습니다.');
      }
      
    } catch (error) {
      console.error('❌ 지갑 연결 해제 중 오류:', error);
      alert('지갑 연결 해제 중 오류가 발생했습니다.');
    }
  };

  // 기능 클릭 핸들러
  const handleFeatureClick = (feature) => {
    if (feature.requiresWallet && !isConnected) {
      connectWallet();
      return;
    }
    feature.onClick();
  };

  // 주소 단축 표시
  const shortenAddress = (address) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  // 기능별 컴포넌트 렌더링
  const renderFeatureComponent = () => {
    switch (selectedFeature) {
      case 'airdrop':
        return (
          <TokenAirdrop 
            account={account} 
            onBack={() => setSelectedFeature(null)}
            user={user}
            userProfile={userProfile}
          />
        );
      case 'transfer':
        return (
          <TokenTransfer 
            account={account} 
            onBack={() => setSelectedFeature(null)}
            user={user}
            userProfile={userProfile}
          />
        );
      case 'nft':
        return (
          <NFTMinting 
            account={account} 
            onBack={() => setSelectedFeature(null)}
            user={user}
            userProfile={userProfile}
          />
        );
      case 'mynft':
        return (
          <MyNFT 
            account={account} 
            onBack={() => setSelectedFeature(null)}
          />
        );  
      case 'gallery':
        return (
          <NFTGallery 
            account={account}
            network={{ rpcUrl: WORLDLAND_NETWORK.rpcUrls[0] }}
            onBack={() => setSelectedFeature(null)}
            // ✅ 수정된 부분: user 객체 전체를 user prop으로 전달
            user={user} 
          />
        );   
      case 'info':
        return (
          <div className="feature-component">
            <div className="feature-header">
              <button onClick={() => setSelectedFeature(null)} className="back-btn">
                <ArrowLeft className="icon" />
                뒤로가기
              </button>
              <h2>Token Info</h2>
            </div>
            <div className="coming-soon">
              <p>토큰 정보 기능은 준비 중입니다.</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (selectedFeature) {
    return renderFeatureComponent();
  }

  return (
    <div className="dapp">
      {/* Header */}
      <div className="dapp-header"> 
        <div 
          onClick={() => navigate('/')}
          className="common-back-button"
        >
          <ChevronLeft size={24} />
        </div>
        <h1>WorldLand Dapp</h1>
      </div>

      {/* 방문자 통계 섹션 추가 */}
      <VisitStats 
        pageName="dapp" 
        user={user}
        className="inline"
      />

      {/* Wallet Section */}
      {/* <div className="wallet-section">
        <div className="wallet-card">
          <div className="wallet-header">
            <Wallet className="wallet-icon" />
            <h2>지갑 연결</h2>
          </div>
          
          {isConnected ? (
            <div className="wallet-connected">
              <div className="connection-status">
                <CheckCircle className="status-icon connected" />
                <span>연결됨</span>
              </div>
              <div className="account-info">
                <p className="account-label">계정 주소</p>
                <p className="account-address">{shortenAddress(account)}</p>
              </div>
              <div className="balance-info">
                <p className="balance-label">WLC 잔액</p>
                <p className="balance-amount">{balance} WLC</p>
              </div>
              <button onClick={disconnectWallet} className="disconnect-btn">
                연결 해제
              </button>
            </div>
          ) : (
            <div className="wallet-disconnected">
              <div className="connection-status">
                <AlertCircle className="status-icon disconnected" />
                <span>연결되지 않음</span>
              </div>
              <p className="connection-description">
                지갑을 연결하여 WorldLand 네트워크의 Dapp 서비스를 이용하세요.
              </p>
               
              {showWalletSelector && (
                <div className="wallet-selector">
                  <h3>지갑 선택</h3>
                  <div className="wallet-options">
                    {availableWallets.map((wallet, index) => (
                      <button
                        key={index}
                        onClick={() => connectWithWallet(wallet)}
                        className="wallet-option"
                        disabled={isConnecting}
                      >
                        <span className="wallet-icon">{wallet.icon}</span>
                        <span>{wallet.name}</span>
                      </button>
                    ))}
                  </div>
                  <button 
                    onClick={() => setShowWalletSelector(false)}
                    className="cancel-btn"
                  >
                    취소
                  </button>
                </div>
              )}
              
              <button 
                onClick={connectWallet} 
                disabled={isConnecting}
                className="connect-btn"
              >
                {isConnecting ? '연결 중...' : '지갑 연결'}
              </button>
            </div>
          )}
        </div>
      </div> */}

      {/* Features Section */}
      <div className="dapp-features">
        <div className="features-header">
          <h2>Dapp 서비스</h2>
          <p>WorldLand 블록체인의 다양한 서비스를 이용하세요</p>
        </div>
        
        <div className="features-grid">
        {dappFeatures.map((feature, index) => {
          // 1. "NFT Minting" 기능에 대한 특정 이메일 조건
          const isRestrictedFeature = feature.title === "NFT Minting";
          const isAuthorizedUser = user && user.email === "gategil1149@gmail.com";

          // 2. 렌더링 제외 조건:
          //    - 해당 기능이 "NFT Minting"인데, 
          //    - 사용자가 지정된 이메일이 아닌 경우 (user 객체가 없거나 이메일이 다를 경우)
          if (isRestrictedFeature && !isAuthorizedUser) {
            return null; // 해당 카드를 렌더링하지 않음
          }

          // 3. 위의 조건에 해당하지 않는 경우 (다른 기능이거나, 권한이 있는 경우) 카드를 렌더링합니다.
          return (
            <div 
              key={index} 
              className="dapp-feature-card clickable"
              onClick={() => handleFeatureClick(feature)}
            >
              <div className="feature-icon">
                <feature.icon className="icon" />
              </div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
};

export default Dapp;